import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPlus, ListChecks } from 'lucide-react';
import { type TaskGroupView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

interface TaskGroupBarProps {
  readonly count: number;
  readonly groups: readonly TaskGroupView[];
  readonly pending: boolean;
  readonly onCreate: (name: string) => void;
  readonly onAssign: (groupId: string) => void;
  readonly onDetach: () => void;
  readonly onClear: () => void;
}

/**
 * Thanh tác vụ cho các task đã tích — APM-1306.
 *
 * ## Vì sao gom nhóm bắt đầu từ việc tích task
 *
 * Nhóm không phải một thứ người ta tạo trước rồi mới nghĩ xem cho gì vào. Trên
 * thực tế người lập kế hoạch nhìn danh sách, thấy năm việc thuộc cùng một pha,
 * rồi mới cần một cái tên cho chúng. Nên đường đi chính là: tích → "Gom thành
 * nhóm mới" → đặt tên. Tạo nhóm rỗng vẫn được (menu "Chuyển vào nhóm" có sẵn
 * mọi nhóm), nhưng nó không phải đường mặc định.
 */
export function TaskGroupBar({
  count,
  groups,
  pending,
  onCreate,
  onAssign,
  onDetach,
  onClear,
}: TaskGroupBarProps): ReactNode {
  const { t } = useTranslation();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  if (count === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-DEFAULT border border-primary/40 bg-primary/5 p-2">
      <ListChecks className="size-4 text-primary" aria-hidden />
      <span className="text-sm font-medium">{t('gantt.selectedCount', { count })}</span>

      {naming ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length === 0) return;
            onCreate(trimmed);
            setName('');
            setNaming(false);
          }}
        >
          {/* Nhóm **phải** có tên: một nhóm không tên thì hôm sau không ai
              biết nó gom gì. Nút bị chặn đến khi có tên. */}
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('gantt.groupNamePlaceholder')}
            aria-label={t('gantt.groupName')}
            className="h-8 w-56 rounded-control border border-border-strong bg-background px-2 text-sm"
          />
          <Button type="submit" size="sm" disabled={pending || name.trim().length === 0}>
            {t('gantt.createGroup')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setNaming(false)}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : (
        <>
          <Button size="sm" disabled={pending} onClick={() => setNaming(true)}>
            <FolderPlus aria-hidden />
            {t('gantt.groupSelected')}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={pending}>
                {t('gantt.moveToExistingGroup')}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuItem onSelect={onDetach}>{t('gantt.removeFromGroup')}</DropdownMenuItem>
              <DropdownMenuSeparator />

              {groups.length === 0 ? (
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('gantt.noGroupYet')}
                </DropdownMenuLabel>
              ) : (
                groups.map((group) => (
                  <DropdownMenuItem key={group.id} onSelect={() => onAssign(group.id)}>
                    <span className="truncate">{group.name}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{group.taskCount}</span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" variant="ghost" onClick={onClear}>
            {t('gantt.clearSelection')}
          </Button>
        </>
      )}
    </div>
  );
}
